// Footer.jsx
import PropTypes from 'prop-types';
import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
} from 'react';
import { withTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import styled, { css } from 'styled-components';
import { sanitize } from 'isomorphic-dompurify';
import parse from 'html-react-parser';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import StatefulImage from '../component/StatefulImage.jsx';

import DecryptionWrapper from '../container/DecryptionWrapper.js';
import LanguageButton from '../container/LanguageButton.js';
import MailTo from '../container/MailTo.js';

import useMessageDetailType from '../hook/useMessageDetailType.js';

import { ButtonId } from '../resource/trackingConstants.js';
import { getShouldShowFooter } from '../resource/getShouldShowFooter.js';
import getIsMatchedPathname from '../resource/getIsMatchedPathname.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getIsValidLink as getIsCompletedUrl } from '../resource/getInternalLink.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../resource/defaults.js';
import env from '../resource/env.js';
import getTranslationKey from '../resource/getTranslationKey.js';

import useBecomeCreatorLink from '../hook/useBecomeCreatorLink.js';

import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { color } from '../style/variables.js';

import AsacpIconSource from '../../img/img_asagp.svg';
import RtaIconSource from '../../img/img_rta.svg';
import CopyIcon from '../../img/ic-copy-link.svg';
import DropDownIcon from '../../img/ic-dropdown-mini.svg';

const isServer = typeof window === 'undefined';

const CONTACT_SUPPORT_LINK = '/contact-support'; // TODO: remote config

const version = env.TAG_NAME;
const shaString = env.SHORT_SHA ? `(${env.SHORT_SHA})` : '';
const branchString = env.BRANCH_NAME ? `(${env.BRANCH_NAME})` : '';
const isOnTagVersion = !/-/.test(version);

const aboutUsTranslationKey = {
  section: 'section',
  title: 'title',
  description: 'description',
  footer: 'footer',
};

export const Footer = ({
  publicLogoUrlPath = '',
  dmcaId = '',
  rtaUrl = '',
  socialMediaLinks: propsSocialMediaLinks = EMPTY_ARRAY,
  aboutLinks: propsAboutLinks = EMPTY_ARRAY,
  supportLinks: propsSupportLinks = EMPTY_ARRAY,
  workLinks: propsWorkLinks = EMPTY_ARRAY,
  discoverLinks: propsDiscoverLinks = EMPTY_ARRAY,
  staticContext = EMPTY_OBJECT,
  footerFAQs = EMPTY_ARRAY,
  footerAboutUs = '',
  shouldShowAboutUsSection = false,
  t,
}) => {
  const { pathname, search } = useLocation();
  const becomeCreatorLink = useBecomeCreatorLink();
  const [expandedDescription, setExpandedDescription] = useState(false);
  const getSectionItemData = useCallback(
    ({ title: titleI18nKey, link, mixpanel, icon }) => {
      const title = t(titleI18nKey, { ns: TranslationNamespace.SETTINGS });
      const { ['button.id']: buttonId, ...extraProps } = mixpanel || {};
      const result = {
        title,
        'data-element_id': buttonId,
        'data-tracking_payload': {
          ...extraProps,
        },
      };
      if (icon) {
        result.icon = getResourceUrl({
          path: publicLogoUrlPath,
          endpoint: `/${icon}.svg`,
          resourceType: PUBLIC,
        });
      }
      const isCompletedUrl = getIsCompletedUrl({
        link,
      });
      if (isCompletedUrl) {
        result.href = link;
      } else {
        result.to = link;
      }
      return result;
    },
    [publicLogoUrlPath, t]
  );
  const aboutLinks = useMemo(
    () => propsAboutLinks.map(getSectionItemData),
    [getSectionItemData, propsAboutLinks]
  );
  const supportLinks = useMemo(
    () => propsSupportLinks.map(getSectionItemData),
    [getSectionItemData, propsSupportLinks]
  );
  const workLinks = useMemo(() => {
    const links = propsWorkLinks.map(getSectionItemData);
    if (becomeCreatorLink) {
      const { isInternalLink, link } = becomeCreatorLink;
      const item = {
        title: t('swaggers_wanted', { ns: TranslationNamespace.SETTINGS }),
        isActive: getIsMatchedPathname({ pathname, dataKey: 'isOnJoin' }),
        'data-element_id': ButtonId.Join.ButtonBecomeCreator,
      };
      if (isInternalLink) {
        item.to = link;
      } else {
        item.href = link;
      }
      links.push(item);
    }
    return links;
  }, [becomeCreatorLink, getSectionItemData, pathname, propsWorkLinks, t]);
  const discoverLinks = useMemo(
    () => propsDiscoverLinks.map(getSectionItemData),
    [getSectionItemData, propsDiscoverLinks]
  );
  const socialMediaLinks = useMemo(
    () => propsSocialMediaLinks.map(getSectionItemData),
    [getSectionItemData, propsSocialMediaLinks]
  );
  const renderSectionItem = item => {
    const { href, to, title } = item;
    if (CONTACT_SUPPORT_LINK === to) {
      return (
        <SectionMailToItem key={'contact-support'} {...item}>
          {title}
        </SectionMailToItem>
      );
    }
    return href ? (
      <SectionExternalLinkItem
        key={href}
        target="_blank"
        rel="noopener noreferrer"
        {...item}
      >
        {title}
      </SectionExternalLinkItem>
    ) : to ? (
      <SectionLinkItem key={to} isActive={pathname === to} {...item}>
        {title}
      </SectionLinkItem>
    ) : (
      <SectionButtonItem key={href} {...item}>
        {title}
      </SectionButtonItem>
    );
  };
  const ssrHostname = staticContext?.requestHeaders?.hostname || 'swag.live';
  const hostname = !isServer ? window.location.hostname : ssrHostname;
  const locationHref = `https://${hostname}${pathname}${search}`;
  const messageLayoutType = useMessageDetailType();
  // Should append refurl on DMCA href query string.
  // Souce code: https://images.dmca.com/Badges/DMCABadgeHelper.min.js
  const dmcaHref = `https://www.dmca.com/Protection/Status.aspx?ID=${dmcaId}&refurl=${locationHref}`;
  const [activeFaqItemId, setActiveFaqItemId] = useState('');
  const handleClickFaqItem = useCallback(({ itemId }) => {
    setActiveFaqItemId(prevActiveFaqItemId =>
      prevActiveFaqItemId === itemId ? '' : itemId
    );
  }, []);
  const getAboutUsTranslationKeyMapping = useCallback(
    translationKey => {
      return t(
        getTranslationKey({ key: `${footerAboutUs}_${translationKey}` }),
        { ns: TranslationNamespace.SEO }
      );
    },
    [footerAboutUs, t]
  );
  const validFooterFAQs = useMemo(
    () =>
      footerFAQs
        .map(itemId => {
          const titleI18nKey = getTranslationKey({
            key: `faq_swag_${itemId}_title`,
          });
          const bodyI18nKey = getTranslationKey({
            key: `faq_swag_${itemId}_description`,
          });
          const itemTitle = t(titleI18nKey, {
            ns: TranslationNamespace.SEO,
          });
          const body = t(bodyI18nKey, {
            ns: TranslationNamespace.SEO,
          });
          const shouldShow =
            itemTitle &&
            itemTitle !== titleI18nKey &&
            body &&
            body !== bodyI18nKey;

          return shouldShow ? { itemId, itemTitle, body } : null;
        })
        .filter(Boolean),
    [footerFAQs, t]
  );

  const validFooterAboutUs = useMemo(() => {
    if (!footerAboutUs) return null;

    const aboutUsSection = getAboutUsTranslationKeyMapping(
      aboutUsTranslationKey.section
    );
    const aboutUsTitle = getAboutUsTranslationKeyMapping(
      aboutUsTranslationKey.title
    );
    const aboutUsDesc = getAboutUsTranslationKeyMapping(
      aboutUsTranslationKey.description
    );

    const titleI18nKey = getTranslationKey({
      key: `${footerAboutUs}_${aboutUsTranslationKey.title}`,
    });
    const descI18nKey = getTranslationKey({
      key: `${footerAboutUs}_${aboutUsTranslationKey.description}`,
    });

    const shouldShow =
      aboutUsTitle &&
      aboutUsTitle !== titleI18nKey &&
      aboutUsDesc &&
      aboutUsDesc !== descI18nKey;

    return shouldShow ? { aboutUsSection, aboutUsTitle, aboutUsDesc } : null;
  }, [footerAboutUs, getAboutUsTranslationKeyMapping]);

  const validAboutUsWhy = useMemo(() => {
    if (!footerAboutUs) return null;

    const aboutUsWhy = getAboutUsTranslationKeyMapping(
      aboutUsTranslationKey.footer
    );
    const whyI18nKey = getTranslationKey({
      key: `${footerAboutUs}_${aboutUsTranslationKey.footer}`,
    });

    const shouldShow = aboutUsWhy && aboutUsWhy !== whyI18nKey;

    return shouldShow ? aboutUsWhy : null;
  }, [footerAboutUs, getAboutUsTranslationKeyMapping]);

  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
  const descriptionRef = useRef(null);

  useEffect(() => {
    setExpandedDescription(false);
  }, [pathname]);

  useEffect(() => {
    if (descriptionRef.current && validAboutUsWhy && !expandedDescription) {
      setIsDescriptionTruncated(
        descriptionRef.current.scrollHeight >
          descriptionRef.current.clientHeight
      );
    }
  }, [validAboutUsWhy, expandedDescription, pathname]);

  if (
    !getShouldShowFooter({
      pathname,
      messageLayoutType,
    })
  ) {
    return null;
  }
  const replaceUrlToPublicDomain = publicDomain => domNode => {
    if (domNode.name === 'a' && domNode.attribs?.href) {
      try {
        const url = new URL(domNode.attribs.href);

        url.hostname = publicDomain;

        domNode.attribs.href = url.toString();
        // eslint-disable-next-line no-empty
      } catch (e) {}
    }
  };
  return (
    <StyledFooter>
      <WrapperFAQAboutUs>
        {validFooterFAQs.length > 0 && (
          <FaqWrapper>
            <SectionContentWrapper>
              <FaqHeader>
                <FaqMainTitle>
                  {t('faq_swag_section', {
                    ns: TranslationNamespace.SEO,
                  })}
                </FaqMainTitle>
                <FaqSubTitle>
                  {t('seo_faq_h1', {
                    ns: TranslationNamespace.SEO,
                  })}
                </FaqSubTitle>
              </FaqHeader>
              <FaqSection $isLast={!validFooterAboutUs && !validAboutUsWhy}>
                <FaqItemList>
                  {validFooterFAQs.map(({ itemId, itemTitle, body }, index) => {
                    const isActive = activeFaqItemId === itemId;

                    return (
                      <FaqItem
                        key={itemId}
                        onClick={() => handleClickFaqItem({ itemId })}
                      >
                        <FaqItemButton
                          data-element_id={ButtonId.All.ButtonExpand}
                          type="button"
                          $isActive={isActive}
                        >
                          <FaqItemTitle $titleType="faq">{`Q${index + 1} : ${itemTitle}`}</FaqItemTitle>
                          <FaqItemIcon
                            src={DropDownIcon}
                            alt="dropdown"
                            width="14"
                            height="14"
                            $isActive={isActive}
                          />
                        </FaqItemButton>
                        <FaqItemBody $isActive={isActive}>
                          {parse(sanitize(body), {
                            replace: replaceUrlToPublicDomain(hostname),
                          })}
                        </FaqItemBody>
                      </FaqItem>
                    );
                  })}
                </FaqItemList>
              </FaqSection>
            </SectionContentWrapper>
          </FaqWrapper>
        )}

        {shouldShowAboutUsSection &&
          validFooterAboutUs &&
          (() => {
            const { aboutUsSection, aboutUsTitle, aboutUsDesc } =
              validFooterAboutUs;
            const isActive = activeFaqItemId === footerAboutUs;

            return (
              <AboutUsSectionWrapper $hasFaq={validFooterFAQs.length > 0}>
                <SectionContentWrapper>
                  <WrapAboutUs $hasDescription={!!validAboutUsWhy}>
                    <FaqSubTitle>{aboutUsSection}</FaqSubTitle>
                    <FaqItem
                      key={footerAboutUs}
                      onClick={() =>
                        handleClickFaqItem({ itemId: footerAboutUs })
                      }
                    >
                      <FaqItemButton
                        data-element_id={ButtonId.All.ButtonExpand}
                        type="button"
                        $isActive={isActive}
                      >
                        <FaqItemTitle $titleType="about">
                          {aboutUsTitle}
                        </FaqItemTitle>
                        <FaqItemIcon
                          src={DropDownIcon}
                          alt="dropdown"
                          width="14"
                          height="14"
                          $isActive={isActive}
                        />
                      </FaqItemButton>

                      <AboutItemContent>
                        <FaqItemBody $isActive={isActive}>
                          {parse(sanitize(aboutUsDesc), {
                            replace: replaceUrlToPublicDomain(hostname),
                          })}
                        </FaqItemBody>
                      </AboutItemContent>
                    </FaqItem>
                  </WrapAboutUs>
                </SectionContentWrapper>
              </AboutUsSectionWrapper>
            );
          })()}

        {validAboutUsWhy && (
          <DescriptionWrapper>
            <SectionContentWrapper>
              <DescriptionContent>
                <ClampText ref={descriptionRef} expanded={expandedDescription}>
                  {parse(sanitize(validAboutUsWhy), {
                    replace: replaceUrlToPublicDomain(hostname),
                  })}
                </ClampText>

                {!expandedDescription && isDescriptionTruncated && (
                  <SeeMoreDescription
                    onClick={() => setExpandedDescription(true)}
                  >
                    ...
                    {t('more', {
                      ns: TranslationNamespace.GENERAL,
                    })}
                  </SeeMoreDescription>
                )}
              </DescriptionContent>
            </SectionContentWrapper>
          </DescriptionWrapper>
        )}
      </WrapperFAQAboutUs>
      <ContentWrapper>
        <Content>
          <Section>
            <SectionTitle>
              {t('about', { ns: TranslationNamespace.SETTINGS })}
            </SectionTitle>
            {aboutLinks.map(renderSectionItem)}
          </Section>
          <Section>
            <SectionTitle>
              {t('footer_support', { ns: TranslationNamespace.SETTINGS })}
            </SectionTitle>
            {supportLinks.map(renderSectionItem)}
          </Section>
          <Section>
            <SectionTitle>
              {t('footer_work', { ns: TranslationNamespace.SETTINGS })}
            </SectionTitle>
            {workLinks.map(renderSectionItem)}
          </Section>
          {discoverLinks.length > 0 && (
            <Section>
              <SectionTitle>
                {t('footer_discover', { ns: TranslationNamespace.SETTINGS })}
              </SectionTitle>
              {discoverLinks.map(renderSectionItem)}
            </Section>
          )}
        </Content>
        <Content isLanguageButton>
          <LanguageButton modalViewRootId={'footer-language-menu'} />
        </Content>
      </ContentWrapper>
      <SubFooterWrapper>
        <SubFooter>
          <SocialMediaLinksSection>
            {socialMediaLinks.map(({ href, icon, title, onClick }) => (
              <SocialMediaLink
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
              >
                <SocialMediaIconWrapper>
                  <StatefulImage>
                    <SocialMediaIcon
                      data-key="loading"
                      src={CopyIcon}
                      alt={title}
                    />
                    <SocialMediaIcon
                      data-key="error"
                      src={CopyIcon}
                      alt={title}
                    />
                    <DecryptionWrapper
                      resourceUrl={icon?.href || icon}
                      data-key="target"
                    >
                      <SocialMediaIcon src={icon} alt={title} />
                    </DecryptionWrapper>
                  </StatefulImage>
                </SocialMediaIconWrapper>
              </SocialMediaLink>
            ))}
          </SocialMediaLinksSection>
          <AssociationLinksSection>
            <AssociationLinks>
              {/* Ref. https://www.rtalabel.org/index.php?content=rtaplus&step=download */}
              <AssociationLink
                href={rtaUrl || '#'}
                target={rtaUrl ? '_blank' : '_self'}
                rel="noopener noreferrer"
                data-element_id={ButtonId.Setting.ButtonRta}
              >
                <RtaIcon src={RtaIconSource} alt="RTA" width="38" height="14" />
              </AssociationLink>
              <AssociationLink
                href="https://www.asacp.org"
                target="_blank"
                rel="noopener noreferrer"
                data-element_id={ButtonId.Setting.ButtonAsacp}
              >
                <AsacpIcon
                  src={AsacpIconSource}
                  alt="ASACP"
                  width="65"
                  height="14"
                  loading="lazy"
                />
              </AssociationLink>
              <AssociationLink
                href={dmcaHref}
                title="DMCA.com Protection Status"
                target="_blank"
                className="dmca-badge"
                data-element_id={ButtonId.Setting.ButtonDmca}
              >
                <DmcaIcon
                  src={`https://images.dmca.com/Badges/DMCA_logo-grn-btn100w.png?ID=${dmcaId}`}
                  alt="DMCA.com Protection Status"
                  width="50"
                  height="18"
                  loading="lazy"
                />
              </AssociationLink>
            </AssociationLinks>
            <CopyrightAndVersion>
              {t('footer_copyright', { ns: TranslationNamespace.SETTINGS })}
              <br />
              {`${version} ${isOnTagVersion ? shaString : branchString}`}
            </CopyrightAndVersion>
          </AssociationLinksSection>
        </SubFooter>
      </SubFooterWrapper>
    </StyledFooter>
  );
};

Footer.propTypes = {
  publicLogoUrlPath: PropTypes.string,
  dmcaId: PropTypes.string,
  rtaUrl: PropTypes.string,
  socialMediaLinks: PropTypes.array,
  aboutLinks: PropTypes.array,
  supportLinks: PropTypes.array,
  workLinks: PropTypes.array,
  discoverLinks: PropTypes.array,
  staticContext: PropTypes.object,
  footerFAQs: PropTypes.array,
  footerAboutUs: PropTypes.string,
  shouldShowAboutUsSection: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

const StyledFooter = styled.footer``;

const FaqWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

const SectionContentWrapper = styled.div`
  max-width: 960px;
  width: 100%;
  ${media.tablet`
    width: 100%;
    padding: 0 16px;
  `};
`;

const WrapperFAQAboutUs = styled.div`
  margin-top: 36px;
  a {
    color: ${color.tealBlue};
    text-decoration: underline;
  }
`;

const FaqHeader = styled.div``;

const FaqMainTitle = styled.h1`
  padding-top: 20px;
  margin: 0px 0px 28px;
  color: ${color.white};
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
  text-align: center;
`;

const FaqSubTitle = styled.h2`
  color: ${color.white};
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: 0.018px;
  margin: 0px 0px 16px;
`;

const FaqSection = styled.section`
  margin-bottom: ${({ $isLast }) => ($isLast ? '24px' : '28px')};
`;

const FaqItemList = styled.div``;

const FaqItem = styled.article`
  margin-bottom: 12px;
  border: 1px solid ${color.neutral[20]};
  border-radius: 4px;
  background-color: ${color.neutral[10]};

  :last-child {
    margin-bottom: 0px;
  }
`;

const FaqItemButton = styled.button`
  ${ResetButtonStyle};
  display: flex;
  width: 100%;
  padding: ${({ $isActive }) =>
    $isActive ? '16px 12px 12px 12px' : '16px 12px 16px 12px'};
  text-align: left;
  align-items: center;
  transition: padding 150ms ease-out;
`;

const FaqItemTitle = styled.h3`
  flex: 1;
  color: ${color.white};
  font-size: ${({ $titleType }) => ($titleType === 'faq' ? '16px' : '18px')};
  font-weight: 600;
  margin: 0;
  line-height: 150%;
  letter-spacing: 0.1%;
`;

const FaqItemIcon = styled.img`
  flex: none;
  width: 14px;
  height: 14px;
  margin-left: 8px;
  object-fit: contain;
  transform: rotate(${({ $isActive }) => ($isActive ? '180deg' : '0deg')});
  transition: transform 150ms ease-out;
`;

const FaqItemBody = styled.div`
  max-height: ${({ $isActive }) => ($isActive ? 'fit-content' : '0px')};
  padding: ${({ $isActive }) => ($isActive ? '0 12px 16px 12px' : '0 12px')};
  overflow: hidden;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
  color: ${color.grey[300]};
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.1%;
  transition:
    max-height 150ms ease-out,
    padding 150ms ease-out,
    opacity 150ms ease-out;
`;

const WrapAboutUs = styled.div`
  margin-bottom: ${({ $hasDescription }) =>
    $hasDescription ? '36px' : '24px'};
`;

const AboutUsSectionWrapper = styled(FaqWrapper)`
  margin-top: ${({ $hasFaq }) => ($hasFaq ? '0' : '24px')};
`;

const AboutItemContent = styled.div`
  p,
  a {
    font-size: 12px;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: 0.012px;
    margin: 0px 0px 12px 0px;
  }

  h3 {
    color: ${color.white};
    font-size: 16px;
    font-weight: 600;
    line-height: 150%;
    letter-spacing: 0.016px;
    margin: 24px 0px 12px;
  }

  h4 {
    color: ${color.white};
    font-size: 14px;
    font-weight: 600;
    line-height: 150%;
    letter-spacing: 0.016px;
    margin: 12px 0px 8px;
  }
  ul {
    padding-left: 24px;
  }
`;

const DescriptionWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const DescriptionContent = styled.div`
  position: relative;
`;

const ClampText = styled.p.attrs(({ expanded }) => ({
  style: {
    maxHeight: expanded ? 'none' : '3em',
    overflow: 'hidden',
  },
}))`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.012px;
  margin: 0;
`;

const SeeMoreDescription = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.012px;
  padding-left: 2px;
  color: ${color.neutral[55]};
  background-color: ${color.neutral[0]};
`;

const ContentWrapper = styled.div`
  background-color: ${color.neutral[10]};
  padding-top: 68px;
  padding-bottom: 20px;
  ${media.mobile`
    padding-top: 64px;
    padding-bottom: 40px;
  `};
`;

const ContentMaxWidth = css`
  margin-left: auto;
  margin-right: auto;
  max-width: 960px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${media.tablet`
    max-width: 720px;
  `};
  ${media.mobile`
    width: 100%;
    max-width: none;
    flex-direction: column;
    justify-content: center;
  `};
`;

const Content = styled.div.attrs(({ isLanguageButton }) => ({
  style: {
    paddingLeft: `${isLanguageButton ? 0 : 16}px`,
    paddingRight: `${isLanguageButton ? 0 : 16}px`,
  },
}))`
  ${ContentMaxWidth};
  margin-bottom: 60px;
  align-items: flex-start;
  ${media.mobile`
    margin-bottom: 56px;
    align-items: center;
  `};
  :last-child {
    margin-bottom: 0px;
  }
`;

const Section = styled.div`
  max-width: 220px;
  ${media.tablet`
    max-width: 180px;
  `};
  ${media.mobile`
    margin-bottom: 60px;
    max-width: none;
    text-align: center;
    :last-child {
      margin-bottom: 0px;
    }
  `};
`;

const SectionTitle = styled.div`
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
  color: ${color.neutral[100]};
`;

const SectionItemStyle = css`
  margin-bottom: 12px;
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: ${({ isActive }) => (isActive ? color.tealBlue : color.neutral[75])};
  text-align: left;
  :last-child {
    margin-bottom: 0px;
  }
  @media (hover: hover) {
    :hover {
      color: ${color.tealBlue};
      opacity: 1;
    }
  }
  ${media.mobile`
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  `};
`;

const SectionMailToItem = styled(MailTo)`
  ${SectionItemStyle};
`;

const SectionButtonItem = styled.button`
  ${ResetButtonStyle};
  ${SectionItemStyle};
`;

const SectionExternalLinkItem = styled.a`
  ${SectionItemStyle};
`;

const SectionLinkItem = styled(({ isActive, ...restProps }) => (
  <Link {...restProps} />
))`
  ${SectionItemStyle};
`;

const SubFooterWrapper = styled.div`
  background-color: ${color.neutral[0]};
  padding-top: 32px;
  padding-bottom: 80px;
  ${media.mobile`
    padding: 40px 28px 60px;
  `};
`;

const SubFooter = styled.div`
  ${ContentMaxWidth};
  align-items: flex-start;
  ${media.mobile`
    align-items: center;
    justify-content: center;
  `};
`;

const SocialMediaLinksSection = styled.div`
  ${media.mobile`
    margin-bottom: 40px;
  `};
`;

const SocialMediaLink = styled.a`
  margin-right: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  :last-child {
    margin-right: 0px;
  }
`;

const SocialMediaIconWrapper = styled.div`
  position: relative;
  width: 32px;
  height: 32px;
`;

const SocialMediaIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const AssociationLinksSection = styled.div``;

const AssociationLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  ${media.mobile`
    justify-content: center;
  `};
`;

const CopyrightAndVersion = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${color.neutral[55]};
  text-align: right;
  white-space: pre-wrap;
  ${media.mobile`
    text-align: center;
  `};
`;

const AssociationLink = styled.a`
  margin-right: 8px;
  :last-child {
    margin-right: 0px;
  }
`;

const RtaIcon = styled.img`
  width: 38px;
  height: 14px;
  object-fit: contain;
`;

const AsacpIcon = styled.img`
  width: 65px;
  height: 14px;
  object-fit: contain;
`;

const DmcaIcon = styled.img`
  width: 50px;
  height: 18px;
  object-fit: contain;
`;

export default withTranslation()(Footer);
